import {fetchDataStatus, getDataStatusQueryKey} from './useFetchDataStatus';

const TERMINAL_BUILD_STORAGE_SUFFIX = 'lastHandledTerminalBuild';

const isRootBuildEntry = entry => (
    typeof entry?.statusEntry === 'string'
    && entry.statusEntry.startsWith('root||')
);

const parseCompletionTime = value => {
    const parsed = Date.parse(value || '');
    return Number.isFinite(parsed) ? parsed : null;
};

export const getTerminalBuilds = processingStatus => (
    Array.isArray(processingStatus)
        ? processingStatus
            .filter(entry => {
                const status = entry?.status || {};
                return isRootBuildEntry(entry)
                    && Boolean(status.completed)
                    && typeof status.success === 'boolean';
            })
            .map(entry => {
                const status = entry.status;
                const processStream = entry.processStream
                    || entry.statusEntry.slice('root||'.length);
                const fingerprint = [
                    processStream,
                    status.process_id || '',
                    status.completed,
                    String(status.success),
                ].join('|');

                return {
                    completedAt: status.completed,
                    fingerprint,
                    processId: status.process_id || '',
                    processStream,
                    success: status.success,
                };
            })
        : []
);

export const getLatestTerminalBuild = (processingStatus, minimumCompletedAt = null) => {
    const minimumTime = parseCompletionTime(minimumCompletedAt);
    const eligible = getTerminalBuilds(processingStatus).filter(build => {
        if (minimumTime === null) return true;
        const completedTime = parseCompletionTime(build.completedAt);
        return completedTime !== null && completedTime >= minimumTime;
    });

    return eligible.sort((left, right) => (
        (parseCompletionTime(right.completedAt) || 0)
        - (parseCompletionTime(left.completedAt) || 0)
    ))[0] || null;
};

export const hasRunningRootBuild = dataStatusResponse => {
    const processingStatus = dataStatusResponse?.dataStatus?.processing_status_new;
    return Array.isArray(processingStatus) && processingStatus.some(entry => {
        const status = entry?.status || {};
        return isRootBuildEntry(entry)
            && Boolean(status.started)
            && !status.completed;
    });
};

export const getTerminalBuildStorageKey = customerId => (
    `${customerId}-${TERMINAL_BUILD_STORAGE_SUFFIX}`
);

export const claimTerminalBuild = ({customerId, terminalBuild, storage = window.localStorage}) => {
    if (!customerId || !terminalBuild?.fingerprint || !storage) return false;

    const storageKey = getTerminalBuildStorageKey(customerId);
    let previous = null;
    try {
        previous = JSON.parse(storage.getItem(storageKey) || 'null');
    } catch (_error) {
        previous = null;
    }

    if (previous?.fingerprint === terminalBuild.fingerprint) return false;

    const previousTime = parseCompletionTime(previous?.completedAt);
    const nextTime = parseCompletionTime(terminalBuild.completedAt);
    if (previousTime !== null && nextTime !== null && nextTime < previousTime) {
        return false;
    }

    storage.setItem(storageKey, JSON.stringify(terminalBuild));
    return true;
};

export const isCustomerConfigurationQuery = (queryKey, {userId, customerId}) => {
    if (!Array.isArray(queryKey) || !customerId) return false;

    const root = queryKey[0];
    if (root === 'data-status' || root === 'forecast-setup') {
        return queryKey[2] === customerId && (!userId || queryKey[1] === userId);
    }

    return [
        `${customerId}_config-arr`,
        `${customerId}_config-rec-types`,
        `${customerId}_config-opp-types`,
        `${customerId}_config-opp-config`,
        `${customerId}_config-pipeline`,
        `${customerId}_stepper-raw-opp-fields`,
        `${customerId}_task-config`,
        `${customerId}_quota-groups`,
    ].includes(root);
};

export const invalidateCustomerConfigurationQueries = (
    queryClient,
    {userId, customerId}
) => {
    if (!queryClient || !customerId) return Promise.resolve();

    return queryClient.invalidateQueries({
        predicate: query => isCustomerConfigurationQuery(
            query.queryKey,
            {userId, customerId}
        ),
        refetchType: 'active',
    });
};

export const resetTanStackForTerminalBuild = async ({
    queryClient,
    userId,
    customerId,
    terminalBuild,
    storage = window.localStorage,
}) => {
    if (!queryClient || !userId || !customerId) return false;
    if (!claimTerminalBuild({customerId, terminalBuild, storage})) return false;

    await queryClient.cancelQueries();
    queryClient.clear();
    if (queryClient.persistor?.removeClient) {
        await queryClient.persistor.removeClient();
    }

    await queryClient.fetchQuery({
        queryKey: getDataStatusQueryKey(userId, customerId),
        queryFn: () => fetchDataStatus(),
        staleTime: 0,
    });
    return true;
};
